import { apiClient } from './apiClient';

const HIGHLIGHTS_API = '/api/app/highlights-extn';

export enum HighlightColor {
  Yellow = 1,
  Green = 2,
  Blue = 3,
  Pink = 4,
  Purple = 5,
  Orange = 6,
  Red = 7,
  Teal = 8,
  Lime = 9,
  Amber = 10,
}

export interface HighlightDto {
  id: string;
  bookId: string;
  userId: string;
  location: string;
  locationLabel: string;
  selectedText: string;
  color: HighlightColor;
  noteText?: string;
  creationTime: string;
  concurrencyStamp?: string;
}

export interface HighlightCreateDto {
  bookId: string;
  location: string;
  locationLabel: string;
  selectedText: string;
  color: HighlightColor;
  noteText?: string;
}

export interface PageNoteCreateDto {
  bookId: string;
  location: string;
  locationLabel: string;
  color: HighlightColor;
  noteText?: string;
  // Note: No selectedText - page-level notes don't have selected text
}

export interface HighlightUpdateDto {
  location: string;
  locationLabel: string;
  selectedText: string;
  color: HighlightColor;
  noteText?: string;
  bookId: string;
  userId: string;
  concurrencyStamp: string;
}

export const highlightsService = {
  /**
   * Get all highlights for current user and specific book
   */
  async getMyHighlights(bookId: string): Promise<HighlightDto[]> {
    const response = await apiClient.get<HighlightDto[]>(
      `${HIGHLIGHTS_API}/my-highlights`,
      { params: { bookId } }
    );
    return response.data;
  },

  /**
   * Get highlights filtered by color
   */
  async getMyHighlightsByColor(
    bookId: string,
    color: HighlightColor
  ): Promise<HighlightDto[]> {
    const response = await apiClient.get<HighlightDto[]>(
      `${HIGHLIGHTS_API}/my-highlights-by-color`,
      { params: { bookId, color } }
    );
    return response.data;
  },

  /**
   * Create a highlight for current user
   */
  async createMyHighlight(input: HighlightCreateDto): Promise<HighlightDto> {
    const response = await apiClient.post<HighlightDto>(
      `${HIGHLIGHTS_API}/my-highlight`,
      input
    );
    return response.data;
  },

  /**
   * Create a page-level note (note without text selection) for current user
   */
  async createPageNote(input: PageNoteCreateDto): Promise<HighlightDto> {
    const response = await apiClient.post<HighlightDto>(
      `${HIGHLIGHTS_API}/page-note`,
      input
    );
    return response.data;
  },

  /**
   * Update a highlight (current user only)
   * Fetches current highlight first to get concurrencyStamp
   */
  async updateMyHighlight(
    id: string,
    bookId: string,
    input: Partial<Omit<HighlightUpdateDto, 'bookId' | 'userId' | 'concurrencyStamp' | 'location' | 'locationLabel' | 'selectedText'>>
  ): Promise<HighlightDto> {
    // Fetch all highlights to find the one we're updating
    const highlights = await this.getMyHighlights(bookId);
    const currentHighlight = highlights.find((h) => h.id === id);

    if (!currentHighlight) {
      throw new Error(`Highlight with id ${id} not found`);
    }

    // Merge with update data
    const updateDto: HighlightUpdateDto = {
      location: currentHighlight.location,
      locationLabel: currentHighlight.locationLabel,
      selectedText: currentHighlight.selectedText,
      color: input.color !== undefined ? input.color : currentHighlight.color,
      noteText: input.noteText !== undefined ? input.noteText : currentHighlight.noteText,
      bookId: currentHighlight.bookId,
      userId: currentHighlight.userId,
      concurrencyStamp: currentHighlight.concurrencyStamp || '',
    };

    const response = await apiClient.put<HighlightDto>(
      `${HIGHLIGHTS_API}/my-highlight/${id}`,
      updateDto
    );
    return response.data;
  },

  /**
   * Delete a highlight (current user only)
   */
  async deleteMyHighlight(id: string): Promise<void> {
    await apiClient.delete(`${HIGHLIGHTS_API}/my-highlight/${id}`);
  },

  /**
   * Get highlight count by color for a book
   */
  async getHighlightCountsByColor(
    bookId: string
  ): Promise<Record<HighlightColor, number>> {
    const response = await apiClient.get<Record<HighlightColor, number>>(
      `${HIGHLIGHTS_API}/counts-by-color`,
      { params: { bookId } }
    );
    return response.data;
  },
};
